/**
 * Renderer baseline (GDD §3.1) and render-resolution policy (GDD §10.3).
 */
import { ACESFilmicToneMapping, PCFShadowMap, SRGBColorSpace, WebGLRenderer } from 'three';
import { config } from '../core/config.ts';

/**
 * GDD §10.3: screens at 2× or less render at their full native pixel ratio, fixed. Screens above 2× render between
 * 1.5× and 2× and may adapt only within that range. Returns [start, min, max].
 */
export function renderDpr(deviceDpr: number): { start: number; min: number; max: number } {
  const { fixedUpTo, adaptiveMin, adaptiveMax } = config.render.dpr;
  const dpr = deviceDpr > 0 ? deviceDpr : 1;
  if (dpr <= fixedUpTo) return { start: dpr, min: dpr, max: dpr };
  return { start: adaptiveMax, min: adaptiveMin, max: adaptiveMax };
}

export function createRenderer(
  canvas: HTMLCanvasElement,
  width: number,
  height: number,
  exposure: number,
): WebGLRenderer {
  const renderer = new WebGLRenderer({ canvas, antialias: true, powerPreference: 'high-performance' });
  renderer.setPixelRatio(renderDpr(window.devicePixelRatio).start);
  renderer.setSize(width, height, false);
  renderer.toneMapping = ACESFilmicToneMapping;
  renderer.toneMappingExposure = exposure;
  renderer.outputColorSpace = SRGBColorSpace;
  renderer.shadowMap.enabled = true;
  // r186 removed PCFSoftShadowMap; its PCFShadowMap is the soft Vogel-disk filter (see the M1 plan, audit).
  renderer.shadowMap.type = PCFShadowMap;
  return renderer;
}
